import React, { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage, useIntl } from '@divvy-web/i18n'
import BasicButton, { BASIC_BUTTON_SIZE_SMALL } from '@divvy-web/skylab.basicbutton'
import Image from '@divvy-web/skylab.image'
import TextLink, { TextLinkSizes } from '@divvy-web/skylab.textlink'

import BillBrandLogo from '@sputnik/components/BillBrandLogo'
import { getIsDSAWebBank } from '@sputnik/components/utils'
import useGetQueryParamsForCreditInit from '@sputnik/pages/PreApplicationPages/useGetQueryParamsForCreditInit'
import { PATHNAME_CONSTS, PRODUCT_CONST_STRINGS, URL } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logInfo } from '@sputnik/utils/loggerUtils'

import styles from './styles.module.scss'

const NotGoodFitResult = () => {
  const [getClassName] = useNameFormatter('NotGoodFitResult')
  // TODO: remove useLocation and useNavigate when removeing show-web-bank canary
  const { search, state, pathname } = useLocation()
  const navigate = useNavigate()
  const { formatMessage } = useIntl()
  const { BILL_SPEND_AND_EXPENSE } = PRODUCT_CONST_STRINGS
  const { BILL_AP_AR } = URL
  const { shouldNavigateToGetToKnowYou, applicantData, handleInitCreditApplication, isLoadingInitCreditApplication } =
    useGetQueryParamsForCreditInit()
  const isWebBank = getIsDSAWebBank(state?.dsaVersion)

  /* tracking start */
  const { tracker } = useWebTracking()

  useEffect(() => {
    tracker?.track('se_signup__good_fit_failed__presented')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* tracking end */

  const onGetStartedClick = () => {
    tracker?.track('se_signup__failure_get_started__triggered', { get_started_window_name: pathname })

    logInfo('onNotGoodFitGetStartedClick', {
      attributes: {
        result: `Sending user to path: ${BILL_AP_AR}`,
      },
      eventName: 'onNotGoodFitGetStartedClickNavigate',
    })
    window.open(BILL_AP_AR, '_blank', 'noopener, noreferrer')
  }

  const navigateToEligibilityRequirements = () => {
    logInfo('learnAboutEligibilityClick', {
      attributes: {
        result: `Sending user to path: ${URL.ELIGIBILITY_REQUIREMENTS_LINK}`,
      },
      eventName: 'navigateToEligibilityRequirements',
    })
  }

  const onClickApplyAnyway = () => {
    if (shouldNavigateToGetToKnowYou) {
      logInfo('applyAnywayClick', {
        attributes: {
          dataFromUP: shouldNavigateToGetToKnowYou,
          result: `No data from UP. Redirecting to ${PATHNAME_CONSTS.GET_TO_KNOW_YOU}`,
        },
        eventName: 'applyAnywayClick',
      })
      navigate({ pathname: PATHNAME_CONSTS.GET_TO_KNOW_YOU, search: search || '' })
    } else {
      logInfo('applyAnywayClick', {
        attributes: {
          dataFromUP: shouldNavigateToGetToKnowYou,
          result: 'Data found from UP. Attempting to init credit application',
        },
        eventName: 'applyAnywayClick',
      })
      handleInitCreditApplication(applicantData)
    }
  }

  return (
    <>
      <BillBrandLogo />
      <div className={styles[getClassName('')]}>
        <div className={styles[getClassName('text-content')]}>
          <div className={styles[getClassName('heading')]}>
            <FormattedMessage
              defaultMessage='{BILL_SPEND_AND_EXPENSE} may not be a good fit for you right now.'
              id='sputnik.NotGoodFitResult__otrndv'
              values={{
                BILL_SPEND_AND_EXPENSE: BILL_SPEND_AND_EXPENSE,
              }}
            />
          </div>
          <div className={styles[getClassName('subtitle')]}>
            <FormattedMessage
              defaultMessage='Based on you responses, we cannot offer a credit line to you at this time. We hope you return and re-apply in the future.'
              id='sputnik.NotGoodFitResult__cgw0Ww'
            />
          </div>
          {!isWebBank && (
            <div className={styles[getClassName('apply-anyways')]}>
              <BasicButton
                disabled={isLoadingInitCreditApplication}
                showSpinner={isLoadingInitCreditApplication}
                size={BASIC_BUTTON_SIZE_SMALL}
                onClick={onClickApplyAnyway}
              >
                <FormattedMessage
                  defaultMessage='Apply anyway'
                  id='sputnik.NotGoodFitResult__F6+ppL'
                />
              </BasicButton>
              <TextLink
                href={URL.ELIGIBILITY_REQUIREMENTS_LINK}
                size={TextLinkSizes.SMALL}
                onClick={navigateToEligibilityRequirements}
              >
                <FormattedMessage
                  defaultMessage='Learn about eligibility'
                  id='sputnik.NotGoodFitResult__eGTQbl'
                />
              </TextLink>
            </div>
          )}
        </div>

        <div className={styles[getClassName('bill-upsell')]}>
          <Image
            alt={formatMessage({
              defaultMessage:
                'person happily looking at a tablet, surrounded by application notifications and progress bars',
              id: 'sputnik.NotGoodFitResult__a5N3Qu',
            })}
            className={styles[getClassName('bill-upsell-image')]}
            src='https://app.divvy.co/assets/stock-photos/credit-application-not-a-good-fit-marketing-content.png'
          />
          <div className={styles[getClassName('bill-upsell-content')]}>
            <div className={styles[getClassName('bill-upsell-content-title')]}>
              <FormattedMessage
                defaultMessage='Supercharge your AP & AR'
                id='sputnik.NotGoodFitResult__Rdqrfr'
              />
            </div>
            <div className={styles[getClassName('bill-upsell-content-text')]}>
              <FormattedMessage
                defaultMessage='Automate your payables and receivables in one smart platform. Create and pay bills, send invoices, and get paid 2x faster. Less busywork, more business.'
                id='sputnik.NotGoodFitResult__5YdVXn'
              />
            </div>
            <div>
              <BasicButton
                size={BASIC_BUTTON_SIZE_SMALL}
                onClick={onGetStartedClick}
              >
                <FormattedMessage
                  defaultMessage='Get started'
                  id='sputnik.NotGoodFitResult__/aBLH2'
                />
              </BasicButton>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default NotGoodFitResult
